import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as UserService from '_services/user-service';
import * as UpdatesService from '_services/updates-service';

const initialState = {
  airportInfo: null,
  user: null,
  dbInfo: null,
  iata: null,
  travelAgent: false,
  profileBubble: null,
  profileBubbleStatus: 'idle',
  status: 'idle',
  loginStatus: 'idle',
  error: null,
  airportStatus: 'idle',
  sendEmailStatus: 'idle',
  passwordStatus: 'idle',
  emailStatus: 'idle',
  nameStatus: 'idle',
  signupStatus: 'idle',
  positionStatus: 'idle',
  requiresMfa: false,
  mfaFactorId: null,
  mfaPhoneHint: null,
  mfaStatus: 'idle',
  mfaError: null,
  passwordFlag: null,
  emailFlag: null,
  nameFlag: null,
  positionFlag: null,
  firstSignIn: true,
  reviewedAirports: {},
  reviewedAirportsStatus: "idle",
  reviewedAirportsError: null,
};

// Async thunk to get pairs of questions and answers
export const lastNotificationRead = createAsyncThunk('notifications/lastNotificationRead', async (userId) => {
  return await UpdatesService.lastNotificationRead(userId);
});


export const sendEmail = createAsyncThunk('user/sendEmail', async (info) => {
  return UserService.sendEmail(info);
});

// get firebase info for user id
export const getFirebaseInfo = createAsyncThunk('user/firebaseInfo', async (userId) => {
  return UserService.getFirebaseInfo(userId);
});

// change password
export const changePassword = createAsyncThunk('user/changepassword', async (password) => {
  return UserService.changePassword(password);
});

// change email
export const changeEmail = createAsyncThunk('user/changeEmail', async (email) => {
  return UserService.changeEmail(email);
});

// update firebase info
export const changeName = createAsyncThunk('user/changeName', async (name) => {
  return UserService.changeName(name);
});

// update user db info
export const updateUserDb = createAsyncThunk('user/updateUserDb', async (userDb) => {
  return UserService.updateUserDb(userDb);
});

// get db info
export const getDbInfo = createAsyncThunk('user/getDbInfo', async (uid) => {
  return UserService.getDbInfo(uid);
});

// signup
export const signup = createAsyncThunk('user/signup', async (form) => {
  return UserService.signup(form);
});

// login
export const login = createAsyncThunk('user/login', async (userDetails) => {
  return UserService.login(userDetails.email, userDetails.password);
});

// verify MFA code to complete a login (TOTP or SMS)
export const verifyMfa = createAsyncThunk('user/verifyMfa', async (mfaCode) => {
  return UserService.verifyMfa(mfaCode);
});

//logout
export const logout = createAsyncThunk('user/logout', async () => {
  return UserService.logout();
});

// get airport/airline info based on logged user type.
export const fetchAirportInfo = createAsyncThunk('airportInfo/fetchAirportInfo', async (params) => {
  return UserService.getAirportByIata(params);
});

export const fetchReviewedAirportsInfo = createAsyncThunk('airportInfo/fetchReviewedAirportsInfo', async (iata) => {
  const params = { code: iata, isReview: true, airline: false }
  return UserService.getAirportByIata(params);
});

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    authorize: (state, action) => {
      // Store a plain snapshot only — never put the Firebase User instance in
      // Redux. Immer auto-freezes state, which breaks Auth internals (especially
      // MFA resolveSignIn token finalisation) when the same object is currentUser.
      const firebaseUser = action.payload;
      return {
        ...state,
        user: firebaseUser
          ? {
              uid: firebaseUser.uid,
              email: firebaseUser.email ?? null,
              emailVerified: firebaseUser.emailVerified === true,
              displayName: firebaseUser.displayName ?? null,
            }
          : null,
      }
    },
    reset: (state, action) => initialState,
    resetStatus: (state, action) => {
      return {
        ...status,
        nameFlag: null,
        nameStatus: 'idle',
        profileBubbleStatus: 'idle',
        airportStatus: 'idle',
        passwordFlag: null,
        passwordStatus: 'idle',
        emailFlag: null,
        emailStatus: 'idle',
        signupStatus: 'idle',
        sendEmailStatus: 'idle'
      }
    },
    notFirstSignIn: (state, action) => {
      return {
        ...state,
        firstSignIn: false
      }
    },
    setIata: (state, action) => {
      return {
        ...state,
        iata: action.payload,
      }
    },
    setTravelAgent: (state, action) => {
      return {
        ...state,
        travelAgent: action.payload,
      }
    },
    resetUserStatus: (state, action) => {
      return {
        ...state,
        status: 'idle',
      }
    },
    resetLoginStatus: (state, action) => {
      return {
        ...state,
        loginStatus: 'idle',
      }
    },
    resetMfa: (state, action) => {
      return {
        ...state,
        requiresMfa: false,
        mfaFactorId: null,
        mfaPhoneHint: null,
        mfaStatus: 'idle',
        mfaError: null,
      }
    },
    resetSendEmailStatus: (state, action) => {
      return {
        ...state,
        sendEmailStatus: 'idle',
      }
    },
    resetReviewedAirports: state => {
      state.reviewedAirportsStatus = "idle";
      state.reviewedAirportsError = null
    }
  },
  extraReducers: {
    [login.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
        loginStatus: 'loading',
      }
    },
    [login.fulfilled]: (state, action) => {
      // a second factor is required to finish the login
      if (action.payload?.requiresMfa) {
        return {
          ...state,
          status: 'idle',
          loginStatus: 'requiresMfa',
          requiresMfa: true,
          mfaFactorId: action.payload.factorId,
          mfaPhoneHint: action.payload.phoneHint,
          mfaStatus: 'idle',
          mfaError: null,
        }
      }
      return {
        ...state,
        status: 'succeeded',
        loginStatus: 'succeded',
        requiresMfa: false,
      }
    },
    [login.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed',
        loginStatus: 'failed',
        error: action.error.message,
      }
    },
    [verifyMfa.pending]: (state, action) => {
      return {
        ...state,
        mfaStatus: 'loading',
        mfaError: null,
      }
    },
    [verifyMfa.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        loginStatus: 'succeded',
        requiresMfa: false,
        mfaFactorId: null,
        mfaPhoneHint: null,
        mfaStatus: 'succeeded',
        mfaError: null,
      }
    },
    [verifyMfa.rejected]: (state, action) => {
      return {
        ...state,
        mfaStatus: 'failed',
        mfaError: action.error.message,
      }
    },
    [getDbInfo.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
      }
    },
    [getDbInfo.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        dbInfo: action.payload,
      }
    },
    [getDbInfo.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed',
        error: action.error.message,
      }
    },
    [signup.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
        signupStatus: 'loading',
      }
    },
    [signup.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        signupStatus: 'succeeded',
        dbInfo: action.payload,
      }
    },
    [signup.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed',
        signupStatus: 'failed',
        error: action.error.message,
      }
    },
    [changePassword.pending]: (state, action) => {
      return {
        ...state,
        passwordStatus: 'loading',
      }
    },
    [changePassword.fulfilled]: (state, action) => {
      return {
        ...state,
        passwordStatus: 'succeeded',
      }
    },
    [changePassword.rejected]: (state, action) => {
      return {
        ...state,
        passwordStatus: 'failed',
        passwordFlag: action.error.message,
      }
    },
    [changeName.pending]: (state, action) => {
      return {
        ...state,
        nameStatus: 'loading',
      }
    },
    [changeName.fulfilled]: (state, action) => {
      return {
        ...state,
        nameStatus: 'succeeded',
      }
    },
    [changeName.rejected]: (state, action) => {
      return {
        ...state,
        nameStatus: 'failed',
        nameFlag: action.error.message,
      }
    },
    [changeEmail.pending]: (state, action) => {
      return {
        ...state,
        emailStatus: 'loading',
      }
    },
    [changeEmail.fulfilled]: (state, action) => {
      return {
        ...state,
        emailStatus: 'succeeded',
      }
    },
    [changeEmail.rejected]: (state, action) => {
      return {
        ...state,
        emailStatus: 'failed',
        emailFlag: action.error.message,
      }
    },
    [getFirebaseInfo.pending]: (state, action) => {
      return {
        ...state,
        profileBubbleStatus: 'loading',
      }
    },
    [getFirebaseInfo.fulfilled]: (state, action) => {
      return {
        ...state,
        profileBubbleStatus: 'succeeded',
        profileBubble: action.payload,
      }
    },
    [getFirebaseInfo.rejected]: (state, action) => {
      return {
        ...state,
        profileBubbleStatus: 'failed',
      }
      //state.emailFlag = action.error.message;
    },
    [sendEmail.pending]: (state, action) => {
      return {
        ...state,
        sendEmailStatus: 'loading',
      }
    },
    [sendEmail.fulfilled]: (state, action) => {
      return {
        ...state,
        sendEmailStatus: 'succeeded',
      }
    },
    [sendEmail.rejected]: (state, action) => {
      return {
        ...state,
        sendEmailStatus: 'failed',
      }
    },
    [fetchAirportInfo.pending]: (state, action) => {
      return {
        ...state,
        airportStatus: 'loading',
      }
    },
    [fetchAirportInfo.fulfilled]: (state, action) => {
      return {
        ...state,
        airportStatus: 'succeeded',
        airportInfo: action.payload,
      }
    },
    [fetchAirportInfo.rejected]: (state, action) => {
      return {
        ...state,
        airportStatus: 'failed'
      }
    },
    [updateUserDb.pending]: (state, action) => {
      return {
        ...state,
        loading: 'loading',
      }
    },
    [updateUserDb.fulfilled]: (state, action) => {
      return {
        ...state,
        status: 'succeeded',
        dbInfo: action.payload,
      }
    },
    [updateUserDb.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed'
      }
    },
    [lastNotificationRead.pending]: (state, action) => {
      return {
        ...state,
        status: 'loading',
        error: null,
      }
    },

    [lastNotificationRead.fulfilled]: (state, action) => {
      return {
        ...state,
        dbInfo: action.payload,
        status: 'succeeded',
        error: null
      }
    },

    [lastNotificationRead.rejected]: (state, action) => {
      return {
        ...state,
        status: 'failed',
        error: action.error.message,
      }
    },
    [fetchReviewedAirportsInfo.pending]: (state, action) => {

      state.reviewedAirportsStatus = 'loading',
        state.reviewedAirportsError = null
    },
    [fetchReviewedAirportsInfo.fulfilled]: (state, action) => {
      if (!state.reviewedAirports) state.reviewedAirports = {}
      state.reviewedAirports[action.payload.iata] = action.payload,
        state.reviewedAirportsStatus = 'succeeded',
        state.reviewedAirportsError = null
    },

    [fetchReviewedAirportsInfo.rejected]: (state, action) => {

      state.reviewedAirportsStatus = 'failed',
        state.reviewedAirportsError = action.error.message
    }
  }
});

export const selectFirebaseInfo = (state) => {

  const fakeUser = {
    email: " ",
    firstName: " ",
    iata: " ",
    isVarified: false,
    lastName: " ",
    location: " ",
    name: "Airport",
    password: " ",
    uid: " "
  };

  if (state.user.dbInfo == null) { return fakeUser; }
  if (state.user.dbInfo == undefined) { return fakeUser; }

  return state.user.dbInfo;
}


export const { authorize, reset, resetStatus, notFirstSignIn, setIata, setTravelAgent, resetUserStatus, resetLoginStatus, resetMfa, resetSendEmailStatus, resetReviewedAirports } = userSlice.actions;

export const selectFirebaseUser = state => state.user.user;
export const selectEmailVerified = state => state.user.user.emailVerified;
export const selectUserDb = state => state.user.dbInfo;
export const selectUserStatus = state => state.user.status
export const selectError = state => state.user.error
export const selectIata = state => {
  if (state.user.iata ? state.user.iata : state.user.dbInfo.details.authority) {
    return state.user.iata ? state.user.iata : state.user.dbInfo.details.authority;
  }
};
export const selectProfileBubble = state => state.user.profileBubble;
export const selectProfileBubbleStatus = state => state.user.profileBubbleStatus;
export const selectTravelAgent = state => state.user.travelAgent;
export const selectAirportInfo = state => state.user.airportInfo;
export const selectAirportStatus = state => state.user.airportStatus;
export const selectSendEmailStatus = state => state.user.sendEmailStatus;
export const selectLoginStatus = state => state.user.loginStatus;
export const selectRequiresMfa = state => state.user.requiresMfa;
export const selectMfaFactorId = state => state.user.mfaFactorId;
export const selectMfaPhoneHint = state => state.user.mfaPhoneHint;
export const selectMfaStatus = state => state.user.mfaStatus;
export const selectMfaError = state => state.user.mfaError;
export const selectLastNotificationRead = state => state.user.dbInfo.lastNotificationRead;

// name
export const selectNameStatus = state => state.user.nameStatus;
export const selectNameFlag = state => state.user.nameFlag;
// password
export const selectPasswordStatus = state => state.user.passwordStatus;
export const selectPasswordFlag = state => state.user.passwordFlag;
// email
export const selectEmailStatus = state => state.user.emailStatus;
export const selectEmailFlag = state => state.user.emailFlag;

export const selectSignupStatus = state => state.user.signupStatus;
export const selectFirstSignIn = state => {
  return state.user.firstSignIn
};

export const selectReviewAirportsInfo = state => state.user.reviewedAirports;
export const selectReviewAirportsStatus = state => state.user.reviewedAirportsStatus;

export default userSlice.reducer;